import * as React from "react";
import { FunctionComponent, useEffect, useState } from "react";
import { useForm } from "react-hook-form/dist/index.ie11";
import axios from "axios";
import { HideSpinner, ShowSpinner } from "../../../ts/components/spinner";
import { IFormError } from "../../../ts/models/error/form-error";
import { ICommercetoolsAddress } from "../../../ts/models/billing-address";
import { ShowPopupError } from "../../../ts/components/error-popup";
import { passwordHint, passwordMinLength, passwordPattern } from "../../../ts/constants/form-values";
import * as UserCentrics from "../../../ts/shared/user-centrics";

interface IProps {
	billingAddress: ICommercetoolsAddress;
	siteKey: string;
	successText: string;
	myDataUrl: string;
}

//Der Endpunkt loggt den Kunden per LoginCustomer mit ein, die Seite ringsherum ist aber bereits
//im ausgeloggten Zustand gerendert - Header und Navigation bieten weiterhin "Anmelden" an. Der
//Text darf den Login deshalb nicht als sichtbar eingetreten behaupten, sondern verweist auf den
//nächsten Seitenaufruf, den der Link darunter ausloest. Gleiches gilt fuer die redaktionelle
//Fassung im Property "guestRegisterSuccessText" am Doctype danke.
const DefaultSuccessText =
	"Ihr Kundenkonto wurde angelegt. Ab dem nächsten Seitenaufruf sind Sie automatisch angemeldet.";

//Greift, wenn der Endpunkt weder ErrorMessage noch ValidationErrors liefert - in der Praxis die
//fehlgeschlagene Turnstile-Prüfung, die in UserController.Register ohne ModelState-Fehler in
//GetInvalidModelResponse läuft.
const DefaultErrorText =
	"Ihr Kundenkonto konnte nicht angelegt werden. Bitte lösen Sie das Captcha erneut und versuchen Sie es noch einmal.";

/**
 * Cloudflare verbraucht das Token bei der serverseitigen Prüfung. Ohne Reset schickt jeder weitere
 * Versuch dasselbe Token, das die Verifizierung dann als "timeout-or-duplicate" ablehnt - der
 * Button wäre nach dem ersten Fehlschlag dauerhaft tot. Das Widget ist an dieser Stelle sicher
 * gerendert, weil onSubmit sonst gar nicht bis zum Request kommt.
 */
const ResetTurnstile = (): void => {
	if (window.turnstile) {
		window.turnstile.reset();
	}
};

/**
 * Der Endpunkt kennt drei Fehlerformen: GetErrorResponse setzt nur ErrorMessage (409
 * BenutzernameBereitsVergeben, allgemeiner Fehler), GetInvalidModelResponse nur ValidationErrors
 * (Honeypot, Namensvalidierung aus RegisterModel), und die fehlgeschlagene Turnstile-Prüfung
 * liefert beides nicht. Ohne alle drei Zweige bliebe ein Fehlschlag vollständig unsichtbar:
 * Spinner weg, nichts passiert, der Klick auf "Konto erstellen" sähe wie ein Hänger aus. Gleiches
 * Vorgehen wie im Gast-Adressformular (assets/react/checkout-addresses-guest/components/checkout-addresses-guest.tsx).
 * Eine Inline-Anzeige am Feld gibt es hier bewusst nicht - die serverseitig geprüften Werte
 * (E-Mail, Anrede, Name) stammen aus der Bestellung und sind im Formular nicht editierbar.
 */
const ShowRegisterError = (error: any): void => {
	const object = error?.response?.data as IFormError;

	if (object?.ErrorMessage) {
		ShowPopupError(object.ErrorMessage);
		return;
	}

	const messages = (object?.ValidationErrors ?? [])
		.map((entry) => entry.Value?.[0])
		.filter((message) => !!message);

	ShowPopupError(messages.length > 0 ? messages.join(" ") : DefaultErrorText);
};

export const GuestRegisterForm: FunctionComponent<IProps> = (props: IProps) => {
	const { register, handleSubmit, errors, watch } = useForm();
	const [showCaptchaError, setShowCaptchaError] = useState(false);
	const [showConsentError, setShowConsentError] = useState(false);
	const [isRegistered, setIsRegistered] = useState(false);

	useEffect(() => {
		UserCentrics.initCloudflare();
	}, [props.siteKey]);

	function postData(data: Record<string, any>) {
		axios
			.post(`/umbraco/api/user/register`, data)
			.then(() => {
				HideSpinner();
				setIsRegistered(true);
			})
			.catch((error: any) => {
				HideSpinner();
				ResetTurnstile();
				ShowRegisterError(error);
			});
	}

	function onSubmit(data: Record<string, any>) {
		if (!UserCentrics.IsCloudflareAllowed()) {
			setShowConsentError(true);
			return;
		}
		setShowConsentError(false);

		ShowSpinner();

		if (!window.turnstile || !window.turnstile.getResponse()) {
			setShowCaptchaError(true);
			HideSpinner();
			return;
		}
		setShowCaptchaError(false);

		data.token = window.turnstile.getResponse();

		//E-Mail und Personendaten stammen aus der Rechnungsadresse der gerade abgeschickten
		//Gastbestellung. Der Gast muss deshalb nur noch ein Passwort setzen.
		data.username = props.billingAddress.GuestEmailAddress;
		data.salutation = props.billingAddress.Salutation;
		data.title = props.billingAddress.Title;
		data.name = props.billingAddress.FirstName;
		data.lastname = props.billingAddress.LastName;

		//Leerstring, nicht null: Der Endpunkt baut daraus "Professions = new[] { model.Profession }", und die API
		//lehnt ein Array mit null-Element ab ("The value 'null' is not valid for field 'Professions'"). Das
		//Registrierungsformular im Checkout schickt hier ebenfalls den Leerstring aus professionList.
		data.profession = "";

		postData(data);
	}

	if (isRegistered) {
		return (
			<div className="row">
				<div className="col-12">
					<p className="font--bold">{props.successText ? props.successText : DefaultSuccessText}</p>
					<a className="link--underline" href={props.myDataUrl}>
						Meine Daten verwalten
					</a>
				</div>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="row">
				<div className="col-12">
					<div className="form-group row">
						<label className="col-sm-4 col-form-label">
							E-Mail-Adresse<span className="mandatory">*</span>
						</label>
						<div className="col-sm-8">
							<p className="col-form-label">{props.billingAddress.GuestEmailAddress}</p>
						</div>
					</div>

					<div className="form-group row" style={{ display: "none" }}>
						<label className="col-sm-4 col-form-label">Passwort</label>
						<div className="col-sm-8">
							<input
								autoComplete="off"
								type="text"
								className="form-control"
								name="password_321"
								placeholder=""
								ref={register({ required: false })}
							/>
						</div>
					</div>

					<div className="form-group row">
						<label className="col-sm-4 col-form-label">
							Passwort<span className="mandatory">*</span>
						</label>
						<div className="col-sm-8">
							<input
								type="password"
								className="form-control"
								name="password"
								placeholder="Passwort"
								autoComplete="new-password"
								ref={register({
									required: true,
									minLength: passwordMinLength,
									pattern: passwordPattern
								})}
							/>
							{errors.password && <div className="form-error">Bitte geben Sie ein gültiges Passwort ein.</div>}
						</div>
					</div>

					<div className="form-group row">
						<label className="col-sm-4 col-form-label">
							Passwort wiederholen<span className="mandatory">*</span>
						</label>
						<div className="col-sm-8">
							<input
								type="password"
								className="form-control"
								name="passwordTwo"
								placeholder="Passwort wiederholen"
								autoComplete="new-password"
								ref={register({
									required: true,
									validate: (value) => value === watch("password")
								})}
							/>
							{errors.passwordTwo && <div className="form-error">Passwörter stimmen nicht überein.</div>}
							<p className="info__text">{passwordHint}</p>
						</div>
					</div>

					{/* Cloudflare Turnstile widget */}
					<div className="form-group row">
						<div className="col-sm-4" />
						<div className="col-sm-8 form">
							<div className="cf-turnstile-uc" data-sitekey={props.siteKey} />
							{showCaptchaError && (
								<span className="form-error">Bitte füllen Sie das Captcha aus, um fortzufahren.</span>
							)}
							{showConsentError && (
								<span className="form-error">
									Bitte stimmen Sie der Nutzung von Cloudflare Turnstile zu, um ein Kundenkonto anzulegen.
								</span>
							)}
						</div>
					</div>

					<div className="form-group row">
						<div className="col-sm-4" />
						<div className="col-sm-8">
							<button className="button button__cta button--red">
								<span className="txt">Konto erstellen</span>
								<i className="fas fa-arrow-right icon-fa icon--white" />
							</button>
						</div>
					</div>
				</div>
			</div>
		</form>
	);
};
